import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faCheck, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { FieldLabel, PrimaryBtn, SearchSelect } from '../../components';
import { useProperties } from '../../queries/properties';
import { useConvertProspect } from '../../queries/prospects';
import { P, pillSt } from '../../theme/tokens';
import * as S from './styled';

const STEPS = ['Tenant record created', 'Removed from Prospects', 'Unit marked occupied'];

export default function ConvertBlock({ prospect, agentOptions, canManage }) {
  const navigate = useNavigate();
  const { rows: listings } = useProperties();
  const convert = useConvertProspect();
  const [unitId, setUnitId] = useState('');
  const [chosenAgent, setChosenAgent] = useState('');
  const [done, setDone] = useState(null);
  const units = useMemo(() => listings.filter((l) => !l.tenantName && !l.tenantId), [listings]);

  const preset = agentOptions.includes(prospect.assignedTo) ? prospect.assignedTo : 'Unassigned';
  const agent = chosenAgent || preset;
  const ready = Boolean(unitId) && agent !== 'Unassigned';

  const submit = () =>
    convert.mutate(
      { id: prospect._id, listingId: unitId, agent },
      { onSuccess: (res) => setDone({ unit: res.listing.name, agent }) }
    );

  return (
    <S.ConvertBox $done={Boolean(done)} aria-labelledby="convert-head">
      <header>
        <h3 id="convert-head">Convert to tenant</h3>
        <span>{done ? 'Lease signed — the record now lives in Tenants.' : 'The single path from prospect to tenant.'}</span>
      </header>
      {done ? (
        <>
          <S.Done>
            <span>
              <FontAwesomeIcon icon={faUserCheck} />
            </span>
            <div>
              <strong>{prospect.name} is now a tenant</strong>
              <small>
                {done.unit} · lease Active · agent {done.agent}
              </small>
            </div>
          </S.Done>
          <S.DoneSteps>
            {STEPS.map((s) => (
              <span key={s} style={pillSt(P.success, { fontSize: 11 })}>
                <FontAwesomeIcon icon={faCheck} />
                {s}
              </span>
            ))}
          </S.DoneSteps>
          <S.LinkBtn type="button" onClick={() => navigate('/contacts?type=tenant')}>
            <FontAwesomeIcon icon={faArrowRight} />
            View {prospect.name} in Tenants
          </S.LinkBtn>
        </>
      ) : (
        <>
          <S.ConvertGrid>
            <div>
              <FieldLabel htmlFor="convert-unit">Unit</FieldLabel>
              <SearchSelect
                id="convert-unit"
                placeholder="Select a unit…"
                value={unitId}
                disabled={!canManage}
                onChange={setUnitId}
                options={units.map((u) => ({ value: u._id, label: u.name }))}
              />
            </div>
            <div>
              <FieldLabel htmlFor="convert-agent">Agent assigned</FieldLabel>
              <SearchSelect
                id="convert-agent"
                value={agent}
                disabled={!canManage}
                onChange={setChosenAgent}
                options={agentOptions.map((a) => ({ value: a, label: a }))}
              />
            </div>
          </S.ConvertGrid>
          <S.ConvertActions>
            <PrimaryBtn type="button" disabled={!canManage || !ready || convert.isPending} onClick={submit}>
              <FontAwesomeIcon icon={faUserCheck} />
              Convert to tenant
            </PrimaryBtn>
            <span className="hint">
              {ready
                ? 'Creates the tenant record, marks the unit occupied and removes them from Prospects.'
                : 'Choose an empty unit and a real agent to continue.'}
            </span>
          </S.ConvertActions>
        </>
      )}
    </S.ConvertBox>
  );
}
